import { useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import useTableFiltering, { TableFiltering } from 'shared/components/table/hooks/table-filtering';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';
import SelectedOrganisationTreeNode from 'shared/features/organisation/models/selected-organisation-tree-node';

import { ReadSystemsQuery } from 'features/systems/domain/models/read-systems-query';
import { System } from 'features/systems/domain/models/system';
import SystemFilter from 'features/systems/domain/models/system-filter';
import { SystemFilterValueType } from 'features/systems/domain/models/system-filter-value';
import SystemMonitoringFilter from 'features/systems/domain/models/system-monitoring-filter';
import { useGetMonitorFilters } from 'features/systems/domain/queries/systems-queries';

export interface SystemFiltersHook {
  activeFilters: SystemFilter[];
  searchBarValue: string;
  selectedOrganisationUnits: SelectedOrganisationTreeNode[];
  changeSelectedOrganisationUnits: (selectedOrganisationUnits: SelectedOrganisationTreeNode[]) => void;

  submitSearch: (query: string) => void;
  selectOption: (filterValueType: SystemFilterValueType, keys: string[]) => void;
  clearFilters: () => void;

  monitorFilters: SystemMonitoringFilter[];
  monitorFiltersIsSuccess: boolean;
  getMonitorFilter: (monitoringType: MonitoringType) => SystemMonitoringFilter;
  loadMonitorFilters: () => void;
  selectMonitorFilter: (monitoringType: MonitoringType, selected: boolean) => void;
  isActiveMonitorFilter: (monitoringType: MonitoringType) => boolean;

  readSystemsQuery: ReadSystemsQuery;

  table: TableFiltering<System>;
}

const useSystemFilters = (): SystemFiltersHook => {
  const [activeFilters, setActiveFilters] = useState<SystemFilter[]>([]);
  const table = useTableFiltering<System>({
    defaultSort: {
      isAscending: true,
      property: 'name',
    },
    sessionStorageKey: 'systems-sort',
  });

  const defaultReadSystemsQuery: ReadSystemsQuery = {
    filters: [],
    pagination: table.currentPagination[0],
    sort: table.sortFromSessionStorage ?? { isAscending: true, property: 'name' },
    searchQuery: '',
  };

  const [readSystemsQuery, setReadSystemsQuery] = useState<ReadSystemsQuery>(defaultReadSystemsQuery);

  const { data: monitorFiltersData, isSuccess: monitorFiltersIsSuccess, refetch: refetchMonitorFilters } = useGetMonitorFilters();

  const [systemFiltersFromSessionStorage, setSystemFiltersFromSessionStorage] = useSessionStorage<SystemFilter[]>(
    FilterSessionStorageKeys.SYSTEM_FILTERS,
    [],
  );
  const [selectedOrganisationUnitsFromSessionStorage, setSelectedOrganisationUnitsFromSessionStorage] = useSessionStorage<
    SelectedOrganisationTreeNode[]
  >(FilterSessionStorageKeys.SYSTEM_ORG_UNITS, []);
  const [searchBarValueFromSessionStorage, setSearchBarFromSessionStorage] = useSessionStorage<string>(
    FilterSessionStorageKeys.SYSTEM_SEARCH_BAR,
    '',
  );

  useEffect(() => {
    buildAndSaveQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    table.currentPage,
    table.currentRowsPerPage,
    table.sortFromSessionStorage,
    systemFiltersFromSessionStorage,
    searchBarValueFromSessionStorage,
  ]);

  const submitSearch = (query: string) => {
    setSearchBarFromSessionStorage(query);
    table.resetPaging();
  };

  const selectOption = (filterValueType: SystemFilterValueType, keys: string[]) => {
    let updatedFiltersList = [...readSystemsQuery.filters];
    const filterIndex = updatedFiltersList.findIndex((x) => x.filterValueType === filterValueType);

    if (filterIndex !== -1) {
      updatedFiltersList[filterIndex] = {
        filterValueType: filterValueType,
        values: keys,
      };
    } else {
      updatedFiltersList.push({
        filterValueType: filterValueType,
        values: keys,
      });
    }

    updatedFiltersList = updatedFiltersList.filter((x) => x.values.length > 0);

    setSystemFiltersFromSessionStorage(updatedFiltersList);
    table.resetPaging();
  };

  const changeSelectedOrganisationUnits = (selectedOrganisationUnits: SelectedOrganisationTreeNode[]) =>
    setSelectedOrganisationUnitsFromSessionStorage(selectedOrganisationUnits);

  const clearFilters = () => {
    setActiveFilters([]);
    setSystemFiltersFromSessionStorage([]);
    setSelectedOrganisationUnitsFromSessionStorage([]);
    setSearchBarFromSessionStorage('');
    setReadSystemsQuery(defaultReadSystemsQuery);

    table.resetPaging();
  };

  const getMonitorFilter = (monitoringType: MonitoringType) => {
    const filter = monitorFiltersData?.find((filter) => filter.monitoringFilterType === monitoringType);
    if (filter) {
      return filter;
    }

    return {
      monitoringFilterType: MonitoringType.All,
      count: monitorFiltersData?.reduce((total, filter) => total + filter.count, 0),
    } as SystemMonitoringFilter;
  };

  const isActiveMonitorFilter = (monitoringType: MonitoringType) => {
    const filter = activeFilters.find((x) => x.filterValueType === SystemFilterValueType.Monitor);
    if (!filter) {
      return false;
    }

    return filter.values.find((x) => x === monitoringType) !== undefined;
  };

  const loadMonitorFilters = () => {
    refetchMonitorFilters();
  };

  const selectMonitorFilter = (monitoringType: MonitoringType, selected: boolean) => {
    let activeMonitorFilters = [...(activeFilters.find((x) => x.filterValueType === SystemFilterValueType.Monitor)?.values ?? [])];

    if (selected && activeMonitorFilters.find((x) => x === monitoringType) === undefined) {
      activeMonitorFilters = [...activeMonitorFilters, monitoringType];
    } else if (!selected && activeMonitorFilters.find((x) => x === monitoringType) !== undefined) {
      const activeMonitorFilterIndex = activeMonitorFilters.findIndex((x) => x === monitoringType);
      if (activeMonitorFilterIndex >= 0) {
        activeMonitorFilters.splice(activeMonitorFilterIndex, 1);
      }
    }

    selectOption(SystemFilterValueType.Monitor, activeMonitorFilters);
    loadMonitorFilters();
  };

  const buildAndSaveQuery = () => {
    setActiveFilters(systemFiltersFromSessionStorage);
    setReadSystemsQuery({
      pagination: table.currentPagination[0],
      sort: table.sortFromSessionStorage,
      filters: systemFiltersFromSessionStorage,
      searchQuery: searchBarValueFromSessionStorage,
    });
  };

  return {
    activeFilters,
    searchBarValue: searchBarValueFromSessionStorage,
    selectedOrganisationUnits: selectedOrganisationUnitsFromSessionStorage,
    changeSelectedOrganisationUnits,

    readSystemsQuery,

    selectOption,
    submitSearch,
    clearFilters,

    monitorFiltersIsSuccess,
    monitorFilters: monitorFiltersData ?? [],
    getMonitorFilter,
    loadMonitorFilters,
    selectMonitorFilter,
    isActiveMonitorFilter,

    table,
  };
};

export default useSystemFilters;
